import { useEffect } from 'react'
import { useAuthStore } from '@/stores/auth'
import { getConsultChatWs, type WsErrorData } from '@/features/consult/ws'

function hasValidToken(accessToken: string | null, expiresAt: number | null): boolean {
  return Boolean(accessToken && (!expiresAt || Date.now() < expiresAt))
}

let bootstrapped = false
let activeAuthKey: string | null = null
let expiryTimer: ReturnType<typeof setTimeout> | null = null
let unsubscribeAuth: (() => void) | null = null
let unsubscribeAuthError: (() => void) | null = null

function clearExpiryTimer() {
  if (expiryTimer) clearTimeout(expiryTimer)
  expiryTimer = null
}

function syncConsultWs(accessToken: string | null, expiresAt: number | null) {
  const ws = getConsultChatWs()

  if (!hasValidToken(accessToken, expiresAt)) {
    clearExpiryTimer()
    activeAuthKey = null
    ws.disconnect()
    return
  }

  const nextAuthKey = `${accessToken}:${expiresAt ?? ''}`
  if (activeAuthKey && activeAuthKey !== nextAuthKey) {
    ws.disconnect()
  }
  activeAuthKey = nextAuthKey
  ws.connect()

  clearExpiryTimer()
  if (expiresAt) {
    expiryTimer = setTimeout(
      () => {
        activeAuthKey = null
        ws.disconnect()
      },
      Math.max(0, expiresAt - Date.now()),
    )
  }
}

function startConsultWsBootstrap() {
  if (bootstrapped) return
  bootstrapped = true

  const ws = getConsultChatWs()
  unsubscribeAuthError = ws.on<WsErrorData>('auth_error', () => {
    ws.disconnect()
    useAuthStore.getState().logout()
  })

  unsubscribeAuth = useAuthStore.subscribe((state) => {
    syncConsultWs(state.accessToken, state.expiresAt)
  })

  const { accessToken, expiresAt } = useAuthStore.getState()
  syncConsultWs(accessToken, expiresAt)
}

function stopConsultWsBootstrap() {
  unsubscribeAuth?.()
  unsubscribeAuthError?.()
  unsubscribeAuth = null
  unsubscribeAuthError = null
  clearExpiryTimer()
  activeAuthKey = null
  bootstrapped = false
  getConsultChatWs().disconnect()
}

if (import.meta.hot) {
  import.meta.hot.dispose(stopConsultWsBootstrap)
}

/**
 * 登录态级别的咨询聊天 WS 启动器。
 *
 * 消息页只负责订阅/渲染业务事件；连接生命周期由这里跟随登录态管理。
 */
export function ConsultWsBootstrap() {
  useEffect(() => {
    startConsultWsBootstrap()
  }, [])

  return null
}
